import { Fragment, useState } from "react";
import { App, Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  EditOutlined,
  ExclamationCircleOutlined,
  FileDoneOutlined,
  PrinterOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { moneyToWordsBg } from "../../../../../lib/bgNumberToWords";
import { computeVatBreakdown, netUnitPrice } from "../../../../../lib/vat";
import { R } from "../../orderingTheme";
import {
  DangerButton,
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  SoftPrimaryButton,
} from "../../modalPrimitives";
import { useCancelInvoice } from "../../../../../hooks/useInvoices";
import { printInvoicePos, type Invoice } from "../../../../../api/invoices";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { InvoiceCustomerEditModal } from "./InvoiceCustomerEditModal";

interface Props {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  // Бутонът „Анулирай" се показва само когато модалът се отваря от
  // справките за фактури. От маркиране (TableFooter) и от история на
  // сметките тук не искаме случайно да отвържем сметка от фактура.
  allowCancel?: boolean;
  // Моливът за редакция на получателя — също само от справките. Пипа само
  // снимката customer_* на записа; суми, редове, номер и дати са readonly.
  allowEdit?: boolean;
  // POS печатът от справките е повторен — оригиналът вече е у клиента,
  // затова оттам излиза „ДУБЛИКАТ", а не втори оригинал.
  posPrintMode?: "original" | "duplicate";
}

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return dayjs(iso).format("D.M.YYYY") + " г.";
  } catch {
    return "—";
  }
};

const formatMoney = (v: number | string) => {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

const formatQty = (v: number | string) => {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(3);
};

export const InvoicePreviewModal = ({
  open,
  invoice,
  onClose,
  allowCancel = false,
  allowEdit = false,
  posPrintMode = "original",
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: paymentMethods = [] } = usePaymentMethods();
  const { modal } = App.useApp();
  const message = useOrderingMessage();
  const { notifyPrinterError } = usePrinterNotifications();
  const cancelMutation = useCancelInvoice();
  // Превключва заглавието „ФАКТУРА ОРИГИНАЛ" → „ФАКТУРА ДУБЛИКАТ"
  // само за времетраенето на print dialog-а. След print → restore.
  const [printAsDuplicate, setPrintAsDuplicate] = useState(false);
  const [isPrintingPos, setIsPrintingPos] = useState(false);
  // Редакция на получателя (моливът) — само от справките (allowEdit)
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);

  if (!invoice) return null;

  const triggerPrint = (asDuplicate: boolean) => {
    // Името на PDF-а / dialog-а за печат идва от document.title.
    // Сетваме го временно на „invoice {number}" и го връщаме обратно
    // след като print dialog-ът се затвори. Същото правим и за
    // дубликат-флага.
    setPrintAsDuplicate(asDuplicate);
    const prevTitle = document.title;
    document.title = `invoice-${invoice.invoice_number}`;
    // Ant Modal-ът е centered overlay → пречи на естествената пагинация
    // в print. Местим .invoice-paper директно в body, маркираме body с
    // .printing-invoice (CSS скрива всичко останало), пускаме print, и
    // връщаме paper-а на оригиналното му място. Ползваме placeholder
    // node, за да знаем точно къде да върнем елемента.
    const paper = document.querySelector(
      ".invoice-paper",
    ) as HTMLElement | null;
    let placeholder: Comment | null = null;
    let originalParent: HTMLElement | null = null;
    let wrapperTable: HTMLTableElement | null = null;
    if (paper && paper.parentElement) {
      originalParent = paper.parentElement;
      placeholder = document.createComment("invoice-paper-placeholder");
      originalParent.insertBefore(placeholder, paper);
      // Обвиваме paper-а в <table> с празен <thead>, който се повтаря
      // на всяка отпечатана страница (display: table-header-group) и
      // създава 16mm top margin без CSS @page margin → така няма
      // браузърски header/footer.
      wrapperTable = document.createElement("table");
      wrapperTable.className = "invoice-print-wrapper";
      const thead = document.createElement("thead");
      const trHead = document.createElement("tr");
      const thSpacer = document.createElement("th");
      thSpacer.setAttribute("aria-hidden", "true");
      trHead.appendChild(thSpacer);
      thead.appendChild(trHead);
      const tbody = document.createElement("tbody");
      const trBody = document.createElement("tr");
      const tdBody = document.createElement("td");
      tdBody.appendChild(paper);
      trBody.appendChild(tdBody);
      tbody.appendChild(trBody);
      wrapperTable.appendChild(thead);
      wrapperTable.appendChild(tbody);
      document.body.appendChild(wrapperTable);
      document.body.classList.add("printing-invoice");
    }
    const restore = () => {
      document.title = prevTitle;
      setPrintAsDuplicate(false);
      document.body.classList.remove("printing-invoice");
      if (paper && placeholder && originalParent) {
        originalParent.insertBefore(paper, placeholder);
        placeholder.remove();
      }
      if (wrapperTable && wrapperTable.parentElement) {
        wrapperTable.parentElement.removeChild(wrapperTable);
      }
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    requestAnimationFrame(() => window.print());
  };

  const handlePosPrint = async () => {
    if (isPrintingPos) return;
    setIsPrintingPos(true);
    try {
      await printInvoicePos(invoice.id, {
        duplicate: posPrintMode === "duplicate",
      });
      message.success(t("invoicePreview.printPosSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(
        getApiErrorMessage(error, t("invoicePreview.printPosError")),
      );
    } finally {
      setIsPrintingPos(false);
    }
  };

  const handleCancelInvoice = () => {
    modal.confirm({
      title: t("invoicePreview.cancelConfirmTitle"),
      icon: <ExclamationCircleOutlined style={{ color: R.brand }} />,
      content: t("invoicePreview.cancelConfirmContent", {
        number: invoice.invoice_number,
      }),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      centered: true,
      okButtonProps: {
        style: { background: R.brand, borderColor: R.brand },
      },
      onOk: async () => {
        try {
          await cancelMutation.mutateAsync(invoice.id);
          message.success(t("invoicePreview.cancelSuccess"));
          onClose();
        } catch (error) {
          message.error(
            getApiErrorMessage(error, t("invoicePreview.cancelError")),
          );
        }
      },
    });
  };

  const paymentMethodName = paymentMethods.find(
    (pm) => pm.id === invoice.payment_method_id,
  )?.name_bg;

  const modalWidth = isMobile ? undefined : isTablet ? 720 : 880;

  // Споделена ДДС разбивка (lib/vat.ts) — същият алгоритъм като при издаване и
  // като на бекенда. Нетото/ДДС на ред сумират ТОЧНО на груповите тотали, а
  // те — на съхранените invoice.subtotal_taxable / vat_total.
  const breakdown = computeVatBreakdown(
    invoice.lines.map((l) => ({
      gross: Number(l.total),
      rate: Number(l.vat_rate),
    })),
  );
  const vatGroups = breakdown.groups.filter((g) => g.rate > 0);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "92vh",
        }}
      >
        <style>{`
          /* Print: само .invoice-paper минава към принтера. Скриваме всичко
             друго на страницата (главното UI, header-а на модала, footer-а)
             и развиваме скрол-а на самата фактура, за да се отпечатат
             всички редове, а не само видимата част. */
          /* @page margin: 0 → НИКАКЪВ браузърски header/footer (дата,
             URL, page numbers). Горното поле на всяка страница го
             постигаме с table > thead spacer трик в JS (thead с
             display: table-header-group се повтаря на всяка отпечатана
             страница, а височината му създава top margin). */
          @page { size: A4; margin: 0; }
          @media print {
            body.printing-invoice > *:not(.invoice-print-wrapper) {
              display: none !important;
            }
            body.printing-invoice {
              margin: 0 !important;
              padding: 0 !important;
              background: #FFFFFF !important;
            }
            body.printing-invoice .invoice-print-wrapper {
              width: 100% !important;
              border-collapse: collapse !important;
            }
            body.printing-invoice .invoice-print-wrapper thead {
              display: table-header-group !important;
            }
            body.printing-invoice .invoice-print-wrapper thead th {
              height: 16mm !important;
              padding: 0 !important;
              border: 0 !important;
              background: #FFFFFF !important;
            }
            body.printing-invoice .invoice-print-wrapper td {
              padding: 0 !important;
              border: 0 !important;
              vertical-align: top !important;
            }
            /* Екранни контроли вътре в paper-а (моливът за редакция на
               получателя) не се печатат */
            body.printing-invoice .invoice-no-print {
              display: none !important;
            }
            body.printing-invoice .invoice-paper {
              position: static !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 0 16mm 16mm 16mm !important;
              background: #FFFFFF !important;
              color: #1A1A1A !important;
            }
            /* Branding-ът върви веднага след съдържанието. С
               break-before:avoid + break-inside:avoid гарантираме, че
               НЕ се изолира на нова страница само за себе си. */
            body.printing-invoice .invoice-paper .invoice-branding {
              break-before: avoid !important;
              page-break-before: avoid !important;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
          }
        `}</style>
        <ModalHeader
          icon={<FileDoneOutlined />}
          title={t("invoicePreview.title", {
            number: invoice.invoice_number,
          })}
          subtitle={t("invoicePreview.subtitle", {
            date: formatDate(invoice.issue_date),
          })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          className="invoice-paper"
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isNarrow ? "16px 18px" : "26px 32px",
            // Хартията е ВИНАГИ светла (печатен документ) — литерали,
            // не theme-реактивните R.* стойности.
            background: "#FFFFFF",
            color: "#241F1A",
            // Ползваме app font-а (Plus Jakarta Sans) — наследен от
            // <body>. Без override → preview-то изглежда консистентно с
            // header-а на модала и останалите страници.
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {invoice.cancelled_at && (
            <div
              style={{
                position: "absolute",
                top: "40%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-18deg)",
                fontSize: isNarrow ? 56 : 96,
                fontWeight: 900,
                letterSpacing: 6,
                color: "rgba(220,38,38,0.18)",
                border: "6px solid rgba(220,38,38,0.18)",
                padding: "10px 28px",
                borderRadius: 12,
                pointerEvents: "none",
                userSelect: "none",
                whiteSpace: "nowrap",
                zIndex: 1,
              }}
            >
              {t("invoicePreview.cancelledBadge")}
            </div>
          )}
          {/* Header card: Получател | Изпълнител */}
          <div
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
              columnGap: 12,
              rowGap: 0,
              background: "#EDECE8",
              border: "1px solid #C9C7BF",
              padding: 14,
            }}
          >
            {/* Редакция на получателя — видим бутон горе вдясно на секцията;
                .invoice-no-print го маха от A4 печата */}
            {allowEdit && !invoice.cancelled_at && (
              <button
                type="button"
                onClick={() => setIsEditingCustomer(true)}
                className="invoice-no-print absolute right-2.5 top-2.5 inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[10px] border border-pos-ctl bg-pos-card px-3 text-[12.5px] font-semibold text-pos-accent shadow-[0_1px_3px_rgba(60,40,20,.08)]"
              >
                <EditOutlined className="text-[13px]" />
                {t("common.edit")}
              </button>
            )}
            <PartyBlock
              title={t("invoicePreview.recipient")}
              name={invoice.customer_name}
              address={`${invoice.customer_city}, ${invoice.customer_address}`}
              eik={invoice.customer_eik}
              vat={invoice.customer_vat}
              mol={invoice.customer_mol}
            />
            <PartyBlock
              title={t("invoicePreview.supplier")}
              name={invoice.supplier_name}
              address={invoice.supplier_address}
              eik={invoice.supplier_eik}
              vat={invoice.supplier_vat}
              mol={invoice.supplier_mol}
              bankName={invoice.supplier_bank_name}
              bic={invoice.supplier_bic}
              iban={invoice.supplier_iban}
            />
          </div>

          {/* Title */}
          <h2
            style={{
              margin: "18px 0 14px",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            {invoice.invoice_type === "simplified"
              ? t("invoicePreview.titleSimplified")
              : printAsDuplicate
                ? t("invoicePreview.titleDuplicate")
                : t("invoicePreview.titleOriginal")}
          </h2>

          {/* Document meta row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
              gap: 14,
              background: "#EDECE8",
              border: "1px solid #C9C7BF",
              padding: "10px 14px",
              marginBottom: 14,
            }}
          >
            <MetaCell
              label={t("invoicePreview.docNumber")}
              value={invoice.invoice_number}
            />
            <MetaCell
              label={t("invoicePreview.docDate")}
              value={formatDate(invoice.issue_date)}
            />
            <MetaCell
              label={t("invoicePreview.supplyDate")}
              value={formatDate(invoice.supply_date)}
            />
            <MetaCell
              label={t("invoicePreview.supplyPlace")}
              value={invoice.supply_place ?? "—"}
            />
          </div>

          {/* Lines table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginBottom: 12,
              fontSize: 12.5,
            }}
          >
            <thead>
              <tr style={{ background: "#EDECE8" }}>
                <Th width={40} align="left">
                  №
                </Th>
                <Th align="left">{t("invoicePreview.colDescription")}</Th>
                <Th width={70} align="right">
                  {t("invoicePreview.colVatRate")}
                </Th>
                <Th width={90} align="right">
                  {t("invoicePreview.colQty")}
                </Th>
                <Th width={84} align="right">
                  {t("invoicePreview.colUnitPrice")}
                </Th>
                <Th width={84} align="right">
                  {t("invoicePreview.colTotal")}
                </Th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((l, idx) => (
                <tr
                  key={l.id}
                  style={{
                    borderBottom: "1px solid #E5E3DC",
                  }}
                >
                  <Td align="left">{idx + 1}</Td>
                  <Td align="left">{l.name}</Td>
                  <Td align="right" mono>
                    {Number(l.vat_rate)}%
                  </Td>
                  <Td align="right" mono>
                    {formatQty(l.quantity)}
                  </Td>
                  <Td align="right" mono>
                    {formatMoney(
                      netUnitPrice(Number(l.unit_price), Number(l.vat_rate)),
                    )}
                  </Td>
                  <Td align="right" mono bold>
                    {formatMoney(breakdown.lines[idx].net)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 240px",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "#5E5749",
                  marginBottom: 4,
                }}
              >
                {t("invoicePreview.totalWords")}:
              </div>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                {moneyToWordsBg(breakdown.grandTotal)}
              </div>
            </div>
            {/* Тоталите идват от същата разбивка като редовете → реконсилират се
                винаги (вкл. за стари фактури). Брутото (Обща стойност) е
                инвариант = Σ редове; може само нето/ДДС да се преразпредели с
                цент спрямо първоначално издаденото. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <SumRow
                label={t("invoicePreview.sumTotal")}
                value={formatMoney(breakdown.totalSum)}
              />
              {breakdown.nonTaxable > 0 && (
                <SumRow
                  label={t("invoicePreview.sumNonTaxable")}
                  value={formatMoney(breakdown.nonTaxable)}
                />
              )}
              {vatGroups.map((g) => (
                <Fragment key={`vat-group-${g.rate}`}>
                  <SumRow
                    label={`${t("invoicePreview.sumTaxable")} (${g.rate}%)`}
                    value={formatMoney(g.net)}
                  />
                  <SumRow
                    label={`${t("invoicePreview.sumVat")} (${g.rate}%)`}
                    value={formatMoney(g.vat)}
                  />
                </Fragment>
              ))}
              <SumRow
                label={t("invoicePreview.sumGrand")}
                value={`${formatMoney(breakdown.grandTotal)}`}
                emphasis
              />
            </div>
          </div>

          {/* Footer card: получаване | плащане + коментар + съставил */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
              gap: 0,
              background: "#EDECE8",
              border: "1px solid #C9C7BF",
              padding: 14,
              marginBottom: 30,
            }}
          >
            <div style={{ paddingRight: isNarrow ? 0 : 14 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#5E5749",
                  marginBottom: 4,
                  fontStyle: "italic",
                }}
              >
                {t("invoicePreview.goodsReceived")}
              </div>
              <KeyVal
                label={t("invoicePreview.receivedAt")}
                value={formatDate(invoice.received_at)}
              />
              <KeyVal
                label={t("invoicePreview.receivedBy")}
                value={invoice.received_by ?? "—"}
              />
              <SignatureLine />
            </div>
            <div style={{ paddingLeft: isNarrow ? 0 : 14 }}>
              <KeyVal
                label={t("invoicePreview.paymentMethod")}
                value={paymentMethodName ?? "—"}
              />
              <KeyVal
                label={t("invoicePreview.noteLabel")}
                value={invoice.note ?? "—"}
              />
              <KeyVal
                label={t("invoicePreview.preparedBy")}
                value={invoice.created_by_name || "—"}
              />
              <SignatureLine />
            </div>
          </div>

          {/* Branding footer — последен child в .invoice-paper.
              В print .invoice-paper е flex column с min-height A4 → този
              блок получава margin-top:auto и се „притиска" към долния ръб
              на последната (или единствената) страница. На екрана просто
              стои в края на документа. */}
          <div
            className="invoice-branding"
            style={{
              marginTop: 18,
              textAlign: "center",
              fontSize: 9.5,
              color: "#5E5749",
              letterSpacing: 0.2,
            }}
          >
            Този документ е отпечатан от софтуер на фирма ЪнриълСофт ООД
            {" — "}
            www.unrealsoft.bg
          </div>
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.close")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          {allowCancel && !invoice.cancelled_at && (
            <DangerButton
              label={t("invoicePreview.cancel")}
              onClick={handleCancelInvoice}
              loading={cancelMutation.isPending}
              icon={<StopOutlined />}
            />
          )}
          {allowCancel && invoice.invoice_type !== "simplified" && (
            <SoftPrimaryButton
              label={t("invoicePreview.printDuplicate")}
              onClick={() => triggerPrint(true)}
              icon={<PrinterOutlined />}
            />
          )}
          {/* Анулирана фактура не се печата — BE-то също я отказва. */}
          {!invoice.cancelled_at && (
            <SoftPrimaryButton
              label={t("invoicePreview.printPos")}
              onClick={handlePosPrint}
              loading={isPrintingPos}
              icon={<PrinterOutlined />}
            />
          )}
          <PrimaryButton
            label={t("invoicePreview.print")}
            onClick={() => triggerPrint(false)}
            icon={<PrinterOutlined />}
          />
        </ModalFooter>
      </div>

      {/* Редакция на получателя — вложен модал; фактурата в prevew-то се
          обновява сама, защото идва от ["invoices", "detail", id] */}
      <InvoiceCustomerEditModal
        open={isEditingCustomer}
        invoice={invoice}
        onClose={() => setIsEditingCustomer(false)}
      />
    </Modal>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────

function PartyBlock({
  title,
  name,
  address,
  eik,
  vat,
  mol,
  bankName,
  bic,
  iban,
}: {
  title: string;
  name: string;
  address: string | null;
  eik: string;
  vat: string | null;
  mol: string | null;
  bankName?: string | null;
  bic?: string | null;
  iban?: string | null;
}) {
  const hasBank = Boolean(bankName || bic || iban);
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#5C5A52",
          fontStyle: "italic",
          marginBottom: 2,
        }}
      >
        {title}:
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
        {name}
      </div>
      <div style={{ marginBottom: 8 }}>
        <span
          style={{
            fontSize: 11,
            color: "#5C5A52",
            fontStyle: "italic",
            marginRight: 6,
          }}
        >
          Адрес:
        </span>
        <span style={{ fontWeight: 600 }}>{address || "—"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 6 }}>
        <span style={{ fontStyle: "italic", color: "#5C5A52" }}>ЕИК №:</span>
        <span style={{ fontWeight: 600 }}>{eik}</span>
        <span style={{ fontStyle: "italic", color: "#5C5A52" }}>ДДС №:</span>
        <span style={{ fontWeight: 600 }}>{vat || "—"}</span>
        <span style={{ fontStyle: "italic", color: "#5C5A52" }}>МОЛ:</span>
        <span style={{ fontWeight: 600 }}>{mol || "—"}</span>
        {hasBank && (
          <>
            <span style={{ fontStyle: "italic", color: "#5C5A52" }}>
              Банка:
            </span>
            <span style={{ fontWeight: 600 }}>{bankName || "—"}</span>
            <span style={{ fontStyle: "italic", color: "#5C5A52" }}>BIC:</span>
            <span style={{ fontWeight: 600 }}>{bic || "—"}</span>
            <span style={{ fontStyle: "italic", color: "#5C5A52" }}>IBAN:</span>
            <span style={{ fontWeight: 600, wordBreak: "break-all" }}>
              {iban || "—"}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#5C5A52",
          fontStyle: "italic",
        }}
      >
        {label}:
      </div>
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>{value}</div>
    </div>
  );
}

function Th({
  children,
  width,
  align,
}: {
  children: React.ReactNode;
  width?: number;
  align: "left" | "right";
}) {
  return (
    <th
      style={{
        width,
        textAlign: align,
        padding: "8px 10px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "#5C5A52",
        borderBottom: "1px solid #C9C7BF",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
  bold,
}: {
  children: React.ReactNode;
  align: "left" | "right";
  mono?: boolean;
  bold?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "7px 10px",
        fontFamily: mono ? R.mono : undefined,
        fontVariantNumeric: mono ? "tabular-nums" : undefined,
        fontWeight: bold ? 700 : 400,
      }}
    >
      {children}
    </td>
  );
}

function SumRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "2px 0",
        fontSize: emphasis ? 14 : 12.5,
        fontWeight: emphasis ? 700 : 500,
        borderTop: emphasis ? "1px solid #C9C7BF" : undefined,
        marginTop: emphasis ? 4 : 0,
        paddingTop: emphasis ? 6 : 2,
      }}
    >
      <span style={{ color: emphasis ? "#241F1A" : "#5C5A52" }}>{label}:</span>
      <span
        style={{
          fontFamily: R.mono,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function KeyVal({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        marginBottom: 4,
        fontSize: 12.5,
      }}
    >
      <span
        style={{
          fontStyle: "italic",
          color: "#5C5A52",
          minWidth: 110,
        }}
      >
        {label}:
      </span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function SignatureLine() {
  return (
    <div
      style={{
        marginTop: 18,
        fontSize: 11,
        color: "#5C5A52",
        fontStyle: "italic",
      }}
    >
      Подпис ............................................
    </div>
  );
}

export default InvoicePreviewModal;
